/*
 * Datazoom, Inc ("COMPANY") CONFIDENTIAL
 * Copyright (c) 2017-2018 [Datazoom, Inc.], All Rights Reserved.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 */

import _, { groupBy, map, orderBy, sortBy, sort } from "lodash";
import {
  COL_PLATFORM_IMG_URL,
  COL_IMG_URL,
  COLLECTOR_STATUS,
  COLLECTOR_TYPE,
  COLLECTOR_DATA_SOURCE_TYPE,
  USERS,
  USER_ROLE,
  PLA_GRAYOUT_IMG_URL,
  COL_GRAYOUT_IMG_URL,
} from "../../app/Constants/Constants";
import { SUPER_ADMIN_ROLE, PIPE_IMG_URL } from "../../constants/Constants";
import { checkDecimalPoint } from "../../app/Utilities/utils";

//
//  HERE KEY IS GIVEN IN ACCORDANCE WITH FIELD KEY TO BE PASSED WHEN SORTING THE CORRESPONDING FIELD ─────
//

export const titles = [
  {
    key: "status",
    label: "Status",
  },
  {
    key: "player",
    label: "Collector",
  },
  {
    key: "name",
    label: "Name",
  },
  {
    key: "samplingrate",
    label: "Sampling Rate",
  },
  {
    key: "pipesConnected",
    label: "Pipes",
  },
];

export const titlesV2 = [
  {
    key: "status",
    label: "Status",
  },
  {
    key: "player",
    label: "",
  },
  {
    key: "platform",
    label: "Platform",
  },
  {
    key: "name",
    label: "Name",
  },
  {
    key: "pipesConnected",
    label: "Data Pipes",
  },
  {
    key: "lastModified",
    label: "Last Modified",
  },
];

export const titlesV3 = [
  {
    key: "status",
    label: "Status",
    sortable: true,
    sortKey: "status",
    sortListKey: "status",
  },
  {
    key: "configName",
    label: "Configuration Name",
    sortable: true,
    sortKey: "collectorconfigname",
    sortListKey: "configName",
    statusIcon: true,
  },
  {
    key: "collectorConfig",
    label: "collector",
    sortable: true,
    sortKey: "collector",
    sortListKey: "collectorName",
  },
  {
    key: "mediaPlayerConfig",
    label: "Media Player",
    sortable: true,
    sortKey: "mediaplayer",
    sortListKey: "mediaPlayer",
  },
  {
    key: "adFrameworkConfig",
    label: "Ad Framework",
    sortable: true,
    sortKey: "adframework",
    sortListKey: "adFramework",
  },
  {
    key: "pipesConnected",
    label: "Data Pipes",
  },
  {
    key: "lastModified",
    label: "Last Modified",
    sortKey: "lastModified",
    sortListKey: "lastModified",
    sortable: true,
  },
  {
    key: "project",
    label: "Project",
    sortable: true,
    sortKey: "project",
    sortListKey: "projectName",
  },
];

export const getListViewObj = (collector, auth) => {
  const obj = {
    comId: collector.companyId,
    key: collector.id,
    status: {
      value: collector.disabled,
      view: "status",
      name: "Collection Paused",
    },
    player: {
      name: collector.playerDescription,
      value: `${COL_IMG_URL}${
        collector?.collectorIcon !== null
          ? `${collector.collectorIcon.toLowerCase()}.svg`
          : `${collector?.avatarIcon?.toLowerCase()}.svg`
      }`,
      view: "img",
      platformIcon:
        collector.pluginSupported || collector.plugin
          ? `${COL_IMG_URL}${collector.avatarIcon.toLowerCase()}.svg`
          : null,
      id: collector.playerId,
      isGrayOut: isGrayOut(collector.playerName),
      plugin: collector?.pluginSupported || collector.plugin,
    },
    name: {
      value: collector.name,
    },
    config: {
      value: collector.id,
      view: "copy",
      name: "Copy Collection Key",
    },
    samplingrate: {
      value: collector.samplingRate,
      name: `${collector.samplingRate}% Sampling Rate`,
    },
    pipesConnected: {
      value: [`${PIPE_IMG_URL}`, collector.totalDataPipes || 0],
      view: "imgVal",
      clickHandler: "pipeClickHandler",
      name: "View Data Pipe",
    },
    collectorStatus: collector.status,
    project: {
      value: collector.projectId,
      name: collector.projectName,
    },
    usageMeterReading: collector.usageMeterReading,
  };
  if (auth && auth.user && auth.user.roleName === SUPER_ADMIN_ROLE) {
    obj.company = {
      value: collector.companyName,
      id: collector.companyId,
    };
  }
  return obj;
};

export const getListViewObjV2 = (collector, auth) => {
  const obj = {
    comId: collector.companyId,
    key: collector.id,
    status: {
      value: collector.disabled,
      view: "status",
      name: "Collection Paused",
    },
    playerIcon: collector?.icon,
    platform: collector?.category,
    lastModifiedOn: collector?.lastModifiedOn,
    datapipeDetails: collector?.datapipeDetails,
    player: {
      name: collector.playerDescription,
      value: `${COL_IMG_URL}${
        collector &&
        collector.avatarIcon &&
        `${collector.avatarIcon.toLowerCase()}.svg`
      }`,
      view: "img",
      platformIcon:
        collector.platformIcon !== null
          ? `${
              isGrayOut(collector.playerName)
                ? PLA_GRAYOUT_IMG_URL
                : COL_PLATFORM_IMG_URL
            }${collector.platformIcon}`
          : null,
      id: collector.playerId,
      isGrayOut: isGrayOut(collector.playerName),
    },
    name: {
      value: collector.name,
    },
    config: {
      value: collector.id,
      view: "copy",
      name: "Copy Collection Key",
    },
    samplingrate: {
      value: collector.samplingRate,
      name: `${collector.samplingRate}% Sampling Rate`,
    },
    pipesConnected: {
      value: [`${PIPE_IMG_URL}`, collector.totalDataPipes || 0],
      view: "imgVal",
      clickHandler: "pipeClickHandler",
      name: "View Data Pipe",
    },
    collectorStatus: collector.status,
    project: {
      value: collector.projectId,
      name: collector.projectName,
    },
    usageMeterReading: collector.usageMeterReading,
    category: collector.category,
    collectorIcon: collector.collectorIcon,
    sharedCollectorConfig: collector.sharedCollectorConfig,
    plugin: collector?.plugin,
    adFrameworkName: collector?.adFrameworkName,
  };
  if (auth && auth.user && auth.user.roleName === SUPER_ADMIN_ROLE) {
    obj.company = {
      value: collector.companyName,
      id: collector.companyId,
    };
  }
  return obj;
};
export const getListViewObjV3 = (collector, auth) => {
  const obj = {
    comId: collector.companyId,
    key: collector.id,
    status: {
      value: collector.disabled,
      view: "status",
      name: "Collection Paused",
    },
    collectorConfig: {
      collectorIcon: `${COL_IMG_URL}${
        collector &&
        collector.collectorIcon &&
        `${collector.collectorIcon.toLowerCase()}.svg`
      }`,
      collectorName: collector?.collectorName,
    },
    mediaPlayerConfig: {
      mediaPlayerIcon: `${COL_IMG_URL}${
        collector &&
        collector.collectorIcon &&
        `${collector.avatarIcon.toLowerCase()}.svg`
      }`,
      mediaPlayerName: collector.playerDescription,
      plugin: collector.category !== "CDN" ? collector.plugin : false,
    },
    adFrameworkConfig: {
      adFrameworkIcon: `${COL_IMG_URL}${collector?.adFrameworkIcon}`,
      adFrameworkName: collector.adFrameworkName,
    },

    playerIcon: collector?.icon,
    platform: collector?.category,
    lastModifiedOn: collector?.lastModifiedOn,
    datapipeDetails: collector?.datapipeDetails,
    player: {
      name: collector.playerDescription,
      value: `${COL_IMG_URL}${
        collector &&
        collector.avatarIcon &&
        `${collector.avatarIcon.toLowerCase()}.svg`
      }`,
      view: "img",
      platformIcon:
        collector.platformIcon !== null
          ? `${
              isGrayOut(collector.playerName)
                ? PLA_GRAYOUT_IMG_URL
                : COL_PLATFORM_IMG_URL
            }${collector.platformIcon}`
          : null,
      id: collector.playerId,
      isGrayOut: isGrayOut(collector.playerName),
    },
    configName: {
      value: collector?.name,
      sdkUpdatePreference: collector?.sdkUpdatePreference,
      customSdkBundled: collector?.customSdkBundled,
      latestVersionAvailable: collector?.majorVersionUpdateAvailable,
      sdkBundledStatus: collector?.sdkBundledStatus,
    },
    config: {
      value: collector.id,
      view: "copy",
      name: "Copy Collection Key",
    },
    samplingrate: {
      value: collector.samplingRate,
      name: `${collector.samplingRate}% Sampling Rate`,
    },
    pipesConnected: {
      value: [`${PIPE_IMG_URL}`, collector.totalDataPipes || 0],
      view: "imgVal",
      clickHandler: "pipeClickHandler",
      name: "View Data Pipe",
    },
    collectorStatus: collector.status,
    project: {
      value: collector.projectId,
      name: collector.projectName,
    },
    usageMeterReading: collector.usageMeterReading,
    category: collector.category,
    collectorIcon: collector.collectorIcon,
    sharedCollectorConfig: collector.sharedCollectorConfig,
    plugin: collector?.plugin,
  };
  if (auth && auth.user && auth.user.roleName === SUPER_ADMIN_ROLE) {
    obj.company = {
      value: collector.companyName,
      id: collector.companyId,
    };
  }
  return obj;
};
export const getACollectorObj = (collector) => {
  const applicationCollectorIcon =
    collector?.pluginSupported && collector?.applicationCollectorName !== "CDN";
  const obj = {
    comId: collector.companyId,
    key: collector.id,
    status: {
      value: collector.disabled,
      view: "status",
      name: "Collection Paused",
    },
    player: {
      name: collector.playerName,
      value: `${COL_IMG_URL}${
        collector &&
        collector.collectorIcon &&
        `${collector.collectorIcon.toLowerCase()}.svg`
      }`,
      view: "img",
      platformIcon: collector?.pluginSupported
        ? `${COL_IMG_URL}${collector.mediaPlayerIcon}.svg`
        : null,

      id: collector.playerId,
      heartBeats:
        collector && collector.playHeadFrequency !== undefined
          ? collector.playHeadFrequency / 1000
          : 60,
      version: collector && collector.playerVersion,
      playerDescription: collector && collector.playerDescription,
      isGrayOut: isGrayOut(collector?.playerName),
    },
    name: {
      value: collector.name,
    },
    config: {
      name: "Copy Collection Key",
      value: collector.id,
      configDesc: collector.configDesc,
    },
    samplingRate: {
      value:
        collector && collector.samplingRate !== undefined
          ? collector.samplingRate
          : 100,
      name: `${collector.samplingRate}% Sampling Rate`,
      supportSampling: collector && collector.supportSampling,
    },
    pipesConnected: {
      value: [`${PIPE_IMG_URL}`, collector.totalDataPipes || 0],
      view: "imgVal",
      clickHandler: "pipeClickHandler",
      name: "View Data Pipe",
    },
    collectorStatus: collector.status,
    project: {
      value: collector.projectId,
      name: collector.projectName,
    },
    mileStone: {
      msAd:
        collector && collector.msAd && collector.msAd !== ""
          ? collector.msAd.split(", ")
          : [],
      msContent:
        collector && collector.msContent && collector.msContent !== ""
          ? collector.msContent.split(", ")
          : [],
    },
    qualifiedView: {
      qvAd:
        collector && collector.qvAd && collector.qvAd !== ""
          ? collector.qvAd.split(", ")
          : [],
      qvContent:
        collector && collector.qvContent && collector.qvContent !== ""
          ? collector.qvContent.split(", ")
          : [],
    },
    usageMeterReading: collector.usageMeterReading,
    cmcd: {
      supportCmcd: collector && collector.supportCmcd,
      value: collector.cmcdStatus !== undefined ? collector.cmcdStatus : "OFF",
    },
    appSessionTimeout: {
      supportAppSessionTimeout: collector?.supportAppSessionTimeout,
      value: collector?.appSessionTimeoutMins,
    },
    requireApiKey: collector.requireApiKey,
    apiKey: collector.apiKey,
    activeDataPipes: collector.activeDataPipes,
    totalActivePipes: collector.totalDataPipes,
    lastSeenTraffic: collector.lastSeenTrafficAt,
    appSessionTimeoutMins: collector?.appSessionTimeoutMins,
    adFramework: collector?.adFramework,
    iabOmEnabled: collector?.iabOmEnabled,
    collectorName: collector?.collectorName,
    plugin: collector?.pluginSupported,
    mediaPlayerName: collector?.mediaPlayerName,
    mediaPlayerIcon: collector?.mediaPlayerIcon,
    sdkUrl: collector.sdkUrl,
    latestSdkVersion: collector.latestSdkVersion,
    activeSdkVersion: collector.activeSdkVersion,
    customerType: collector.customerType,
    adframeworkList: collector?.adFrameworks,
    adFrameworkId: collector?.adFrameworkId,
    collectorConfigDomains: collector?.collectorConfigDomains,
    componentVersionDetails: sortByCollectorType(
      collector?.componentVersionDetails,
    ),
    sdkUpdatePreference: collector?.sdkUpdatePreference,
    customSdkBundled: collector?.customSdkBundled,
    sdkBundledStatus: collector?.sdkBundledStatus,
    alternateBrokerUrl: collector?.alternateBrokerUrl,
    restorable: collector?.restorable,
    suppress4xx: collector?.suppress4xx,
  };
  if (applicationCollectorIcon) {
    obj.player = {
      ...obj.player,
      platformIcon: `${COL_IMG_URL}${collector?.playerName?.toLowerCase()}.svg`,
    };
  }
  return obj;
};
export const getBO = /* istanbul ignore next */ (
  auth,
  player,
  collector,
  isDisabledStatus,
  id,
  companyId,
  cmcdStatus,
  samplingRate,
  projectId,
) => {
  let BO;
  /* istanbul ignore next */

  if (auth && player && collector) {
    BO = {
      companyId: companyId || auth.user.company_id,
      playerId: player.id,
      playerName: player.name,
      name: collector.name.replace(/\s\s+/g, " ").trim(),
      playHeadFrequency: collector.frequency * 1000,
      disabled: isDisabledStatus,
      cmcdStatus,
      samplingRate,
      projectId,
    };
    if (collector.info) {
      BO.info = collector.info;
    }
    if (id) {
      BO.id = id;
    }
  } else {
    BO = {};
  }

  return BO;
};
export const getVO = /* istanbul ignore next */ (collector, players) => {
  if (!collector || !players || !players.length) {
    return null;
  }
  const frequency = collector.playHeadFrequency / 1000;
  let player = players.filter((plyr) => plyr.id === collector.playerId);
  player = player[0];
  const VO = {
    samplingRate: collector.samplingRate,
    cmcdStatus: collector.cmcdStatus || "OFF",
    player: player || {},
    collector: {
      name: collector.config_name || "",
      frequency: frequency || 1,
    },
  };
  return VO;
};

export const getACollectorVO = (collector) => {
  const collectorVO = collector
    ? {
        companyid: collector.companyId,
        company_name: collector.companyName,
        id: collector.id,
        name: collector.name,
        icon: collector.icon,
        disabled: collector.disabled,
        playHeadFrequency: collector.playHeadFrequency,
        playerId: collector.playerId,
        playerName: collector.playerName,
        info: collector.info,
        category: collector.category,
        requireApiKey: collector.requireApiKey,
        collectorStatus: collector.status,
        cmcdStatus: collector.cmcdStatus,
        samplingRate: collector.samplingRate,
        projectId: collector.projectId,
        msContent:
          collector &&
          collector?.msContent &&
          collector?.msContent?.split(", "),
        platformIcon: collector.platformIcon,
      }
    : {};
  return collectorVO;
};

export const innerGridVo = /* istanbul ignore next */ (collector) => ({
  id: collector.id,
  title: collector.name,
  url: `${COL_IMG_URL}${collector.icon}`,
  alt: collector.name,
  data: getACollectorVO(collector),
});
export const filterBoxData = (players, category, projectLists, auth) => {
  const sortCaseInsensitive = (a, b, key) => {
    const valA = key ? a[key] : a;
    const valB = key ? b[key] : b;
    if (valA === null || valB === null) return 0;
    return valA.localeCompare(valB, undefined, { sensitivity: "base" });
  };
  const generateId = () => Math.random().toString(36).slice(2, 7);
  const createDPValue = (items, nameKey, typeValue) =>
    items?.map((item) => ({
      id: item?.id || generateId(),
      name: nameKey ? item[nameKey] : item,
      type: typeValue,
    }));
  players?.sort((a, b) => sortCaseInsensitive(a, b, "alt"));
  category?.sort(sortCaseInsensitive);
  projectLists?.sort((a, b) => sortCaseInsensitive(a, b, "name"));

  const DPList = [
    {
      name: "Type",
      DPValue: createDPValue(COLLECTOR_TYPE, null, "col_type"),
    },
    {
      name: "Player/Collector",
      DPValue: createDPValue(players, "alt", "playerName"),
    },
    {
      name: "Category",
      DPValue: createDPValue(category, null, "category"),
    },
    {
      name: "Status",
      DPValue: createDPValue(COLLECTOR_STATUS, null, "status"),
    },
  ];

  if (auth?.user?.roleName !== USER_ROLE) {
    DPList.push({
      name: "Project",
      DPValue: createDPValue(projectLists, "name", "project"),
    });
  }
  return {
    DPList: DPList.filter((filter) => filter?.DPValue?.length),
  };
};

export const filterBoxDataV2 = (players, category, projectLists, auth) => {
  const sortCaseInsensitive = (a, b, key) => {
    const valA = key ? a[key] : a;
    const valB = key ? b[key] : b;
    if (valA === null || valB === null) return 0;
    return valA.localeCompare(valB, undefined, { sensitivity: "base" });
  };
  const generateId = () => Math.random().toString(36).slice(2, 7);
  const createDPValue = (items, nameKey, typeValue) =>
    items?.map((item) => ({
      id: item?.id || generateId(),
      name: nameKey ? item[nameKey] : item,
      type: typeValue,
    }));
  players?.sort((a, b) => sortCaseInsensitive(a, b, "alt"));
  category?.sort(sortCaseInsensitive);
  projectLists?.sort((a, b) => sortCaseInsensitive(a, b, "name"));
  const defaultItem = {
    pluginId: "None",
    pluginName: "None",
    alt: "None",
  };
  if (players) {
    players.unshift(defaultItem);
  }

  const DPList = [
    {
      name: "Platform",
      DPValue: createDPValue(category, null, "collector"),
    },
    {
      name: "Media Player",
      DPValue: createDPValue(players, "alt", "mediaPlayer"),
    },
    {
      name: "Status",
      DPValue: createDPValue(COLLECTOR_STATUS, null, "status"),
    },
  ];

  if (auth?.user?.roleName !== USER_ROLE) {
    DPList.push({
      name: "Project",
      DPValue: createDPValue(projectLists, "name", "project"),
    });
  }
  return {
    DPList: DPList.filter((filter) => filter?.DPValue?.length),
  };
};

export const filterApiData = (filterData) => {
  const type = [];
  const collector = [];
  const category = [];
  const status = [];
  const project = [];
  filterData?.map((data) => {
    switch (data.type) {
      case "col_type":
        type.push(data.name);
        break;
      case "playerName":
        collector.push(data.name);
        break;
      case "category":
        category.push(data.name);
        break;
      case "status":
        status.push(!(data.name === "Active"));
        break;
      case "project":
        project.push(data.id);
        break;
      default:
        break;
    }
  });
  return {
    type,
    collector,
    category,
    status,
    project,
  };
};

const platformNameMap = {
  JavaScript: "Javascript",
  "iOS/tvOS": "iOS / tvOS",
  CDN: "CDN",
  Android: "Android",
  Roku: "Roku",
};

export const filterApiDataV2 = (filterData, createNewKey) => {
  const mediaplayer = [];
  const collector = createNewKey ? ["JavaScript"] : [];
  const status = createNewKey ? [false] : [];
  const project = [];
  filterData?.map((data) => {
    switch (data.type) {
      case "mediaPlayer":
        mediaplayer.push(
          data.name === "None" ? data.name.toLowerCase() : data.name,
        );
        break;
      case "collector": {
        let normalizedName = "";

        if (data.name) {
          const lowerName = data?.name
            ?.toLowerCase()
            ?.replace(/\s+/g, "")
            ?.replace(/ /g, "")
            ?.trim();
          const matchedKey = Object.keys(platformNameMap).find(
            (key) => key?.replace(/\s+/g, "").toLowerCase() === lowerName,
          );
          normalizedName = matchedKey
            ? platformNameMap[matchedKey]
            : data?.name;
        }

        if (!normalizedName) {
          collector.unshift(normalizedName);
        } else {
          collector.push(normalizedName);
        }
        break;
      }

      case "status":
        status.push(!(data.name === "Active"));
        break;
      case "project":
        project.push(data.id);
        break;
      default:
        break;
    }
  });
  return {
    collector,
    mediaplayer,
    status,
    project,
  };
};
export const filterDefaultData = () => {
  return {
    id: Math.random().toString(36).slice(2, 7),
    name: "Active",
    type: "status",
  };
};
export const filterViewProjectName = (projectId, project) => {
  return {
    id: projectId,
    name: project.projectName,
    type: "project",
  };
};

export const filterDataSourceBox = (players) => {
  players &&
    players.sort((a, b) => {
      return a.alt.localeCompare(b.alt);
    });

  return {
    DPList: [
      {
        name: "Collector",
        DPValue:
          players &&
          players.map((playerName) => {
            return {
              id: playerName.id,
              name: playerName.alt,
              type: "alt",
            };
          }),
      },
      {
        name: "Collector Type",
        DPValue: COLLECTOR_DATA_SOURCE_TYPE.map((type) => {
          return {
            id: Math.random().toString(36).slice(2, 7),
            name: type,
            type: "type",
          };
        }),
      },
      {
        name: "Platform",
        DPValue: _(players)
          .filter((player) => player.category)
          .sort((a, b) => a.category.localeCompare(b.category))
          .groupBy("category")
          .map((value, categoryName) => {
            return {
              id: Math.random().toString(36).slice(2, 7),
              name: categoryName,
              type: "category",
            };
          })
          // .reverse()
          .value(),
      },
    ],
  };
};
const playerData = [
  "akamaiamp_tvos_gold",
  "android_exo_player",
  "ios_native_gold",
  "akamaiamp_ios",
  "tvos_native_gold",
  "akamaiamp_android",
  "apple_avplayer",
];

export const isGrayOut = (playerName) => {
  return playerData?.includes(playerName);
};
export const extractPlatformIcon = (iconPath) => {
  const collectorsPrefix = "/images/collectors-player/";
  const platformPrefix = "/images/platform-player/";
  if (iconPath.startsWith(platformPrefix)) {
    return iconPath.slice(platformPrefix.length);
  }
  if (iconPath.startsWith(collectorsPrefix)) {
    return iconPath.slice(collectorsPrefix.length);
  }
  return iconPath;
};
export const filterDataSource = (filterData) => {
  const type = [];
  const alt = [];
  const category = [];
  filterData.map((data) => {
    switch (data.type) {
      case "type":
        type.push(data.name);
        break;
      case "alt":
        alt.push(data.name);
        break;
      case "category":
        category.push(data.name);
        break;
      default:
        break;
    }
  });
  return {
    type,
    alt,
    category,
  };
};

export const collectorConfigStatsFormat = (collector) => {
  const collectionSharePercentage = checkDecimalPoint(
    collector.summary.collection_share_percentage,
  );
  const connectionSharePercentage = checkDecimalPoint(
    collector.summary.connection_share_percentage,
  );
  const totalShare =
    100 - (collectionSharePercentage + connectionSharePercentage);
  const totalAvaliableSpacePercentage = Number(totalShare.toFixed(2));
  const data = {
    name: "collector",
    fromDate: collector.from_date,
    toDate: collector.to_date,
    id: collector.config_id,
    summary: collector.summary,
    aggType: "DAY",
    breakdown: collector.breakdown,
    summaryUsage: {
      series: [
        totalAvaliableSpacePercentage,
        connectionSharePercentage,
        collectionSharePercentage,
      ],
      labels: ["available", "connection", "collection"],
      colors: ["rgba(79,91,98,0.07)", "#004f98", "#5ba9fd"],
    },
    breakdownUsage: {
      colors: ["#5ba9fd", "#004f98"],
      series: [
        {
          name: "collection",
          data: collector.breakdown.map((value) => {
            return {
              x: value.date,
              y: value.collection !== null ? value.collection : 0,
            };
          }),
        },
        {
          name: "connection",
          data: collector.breakdown.map((value) => {
            return {
              x: value.date,
              y: value.connection !== null ? value.connection : 0,
            };
          }),
        },
      ],
    },
  };
  return data;
};

export const getInitialCollectorValuev2 = (initialValue) => {
  const VO = {
    playHeadFrequency: initialValue.player.heartBeats * 1000,
    samplingRate: initialValue.samplingRate.value,
    cmcdStatus: initialValue.cmcd.value,
    msAd: initialValue.mileStone.msAd.sort((a, b) => a - b).join(", "),
    msContent: initialValue.mileStone.msContent
      .sort((a, b) => a - b)
      .join(", "),
    qvAd: initialValue.qualifiedView.qvAd.sort((a, b) => a - b).join(", "),
    qvContent: initialValue.qualifiedView.qvContent
      .sort((a, b) => a - b)
      .join(", "),
    appSessionTimeoutMins: initialValue.appSessionTimeout.value,
    adFrameworkId:
      initialValue.adFrameworkId !== " " ? initialValue.adFrameworkId : null,
    collectorConfigDomains: [
      ...new Set(initialValue?.collectorConfigDomains || []),
    ].join(","),
    alternateBrokerUrl: initialValue?.alternateBrokerUrl ?? null,
  };
  return VO;
};

export const getUpdatedCollectorValuev2 = (
  adFramework,
  updatedValue,
  heartBeat,
  samplingRate,
  cmcdChecked,
  msAd,
  msContent,
  qvAd,
  qvContent,
) => {
  /* istanbul ignore next */
  const VO = {
    adFrameworkId: adFramework !== " " ? adFramework : null,
    playHeadFrequency:
      heartBeat !== ""
        ? heartBeat * 1000
        : updatedValue.player.heartBeats * 1000,
    samplingRate:
      samplingRate !== "" ? samplingRate : updatedValue.samplingRate.value,
    cmcdStatus: cmcdChecked
      ? updatedValue.cmcd.value === "OFF"
        ? "LITE"
        : updatedValue.cmcd.value
      : cmcdChecked === ""
      ? updatedValue.cmcd.value !== "OFF"
        ? updatedValue.cmcd.value
        : "OFF"
      : "OFF",
    msAd: msAd.sort((a, b) => a - b).join(", "),
    msContent: msContent.sort((a, b) => a - b).join(", "),
    qvAd: qvAd.sort((a, b) => a - b).join(", "),
    qvContent: qvContent.sort((a, b) => a - b).join(", "),
    appSessionTimeoutMins: updatedValue.appSessionTimeout.value,
    collectorConfigDomains: [
      ...new Set(updatedValue?.collectorConfigDomains || []),
    ].join(","),
    alternateBrokerUrl: updatedValue?.alternateBrokerUrl ?? null,
  };
  return VO;
};
export const getInitialCollectorValue = (initialValue) => {
  const VO = {
    playHeadFrequency: initialValue.player.heartBeats * 1000,
    samplingRate: initialValue.samplingRate.value,
    cmcdStatus: initialValue.cmcd.value,
    msAd: initialValue.mileStone.msAd.sort((a, b) => a - b).join(", "),
    msContent: initialValue.mileStone.msContent
      .sort((a, b) => a - b)
      .join(", "),
    qvAd: initialValue.qualifiedView.qvAd.sort((a, b) => a - b).join(", "),
    qvContent: initialValue.qualifiedView.qvContent
      .sort((a, b) => a - b)
      .join(", "),
    appSessionTimeoutMins: initialValue.appSessionTimeout.value,
    adFramework: initialValue.adFramework,
  };
  return VO;
};

export const getUpdatedCollectorValue = (
  adFramework,
  updatedValue,
  heartBeat,
  samplingRate,
  cmcdChecked,
  msAd,
  msContent,
  qvAd,
  qvContent,
) => {
  /* istanbul ignore next */
  const VO = {
    adFramework,
    playHeadFrequency:
      heartBeat !== ""
        ? heartBeat * 1000
        : updatedValue.player.heartBeats * 1000,
    samplingRate:
      samplingRate !== "" ? samplingRate : updatedValue.samplingRate.value,
    cmcdStatus: cmcdChecked
      ? updatedValue.cmcd.value === "OFF"
        ? "LITE"
        : updatedValue.cmcd.value
      : cmcdChecked === ""
      ? updatedValue.cmcd.value !== "OFF"
        ? updatedValue.cmcd.value
        : "OFF"
      : "OFF",
    msAd: msAd.sort((a, b) => a - b).join(", "),
    msContent: msContent.sort((a, b) => a - b).join(", "),
    qvAd: qvAd.sort((a, b) => a - b).join(", "),
    qvContent: qvContent.sort((a, b) => a - b).join(", "),
    appSessionTimeoutMins: updatedValue.appSessionTimeout.value,
  };
  return VO;
};

export const processDataPointList = (
  datapointGroupMap,
  groupName,
  mainGroup,
  currentDatapipeConfig,
) => {
  const listNode = [];
  // Iterate over each datapoint in the group map
  datapointGroupMap.forEach((datapoint) => {
    const dataPointName =
      datapoint.label !== null ? datapoint.label : datapoint.name;

    // Initialize the item based on the current datapoint
    const item = {
      dataPointId: datapoint.dataPointId,
      name: dataPointName,
      label: datapoint.name,
      deprecated: datapoint.deprecated,
      required: datapoint.required,
      group: groupName,
      mainGroup,
      selected: datapoint.selected,
    };
    // If the group is "Metadata", add subgroup details
    if (groupName === "Metadata") {
      item.subGroup = datapoint.group;
    }
    // If the group is "Events", add media type details
    if (groupName === "Events") {
      item.supportedMediaTypes = datapoint?.supportedMediaTypes;
      item.selectedMediaTypes = datapoint?.selectedMediaTypes;
      item.datapointsList = datapoint?.datapointsList;
    }
    // Now check if there is a matching datapoint in datapipeConfig
    if (currentDatapipeConfig && Array.isArray(currentDatapipeConfig)) {
      const matchingConfig = currentDatapipeConfig.find(
        (config) => config.dataPointId === datapoint.dataPointId,
      );
      if (matchingConfig) {
        // Update the item based on the datapipeConfig if there's a match
        item.selected = matchingConfig.required || matchingConfig.selected;
        if (groupName === "Events") {
          item.selectedMediaTypes = matchingConfig.selectedMediaTypes;
        }
      }
    }
    // Push the updated item to listNode
    listNode.push(item);
    listNode.sort((a, b) => a.name.localeCompare(b.name));
  });
  return { listNode };
};

export const filterBoxCollMgmt = (
  DataPointType,
  DataPointUIGroup,
  MediaType,
  Supported,
) => {
  const sortCaseInsensitive = (a, b, key) => {
    const valA = key ? a[key] : a;
    const valB = key ? b[key] : b;
    if (valA === null || valB === null) return 0;
    return valA.localeCompare(valB, undefined, { sensitivity: "base" });
  };
  DataPointType?.sort(sortCaseInsensitive);
  DataPointUIGroup?.sort(sortCaseInsensitive);
  MediaType?.sort(sortCaseInsensitive);
  Supported?.sort(sortCaseInsensitive);
  const generateId = () => Math.random().toString(36).slice(2, 7);
  const createDPValue = (items, nameKey, typeValue) =>
    items?.map((item) => ({
      id: item?.id || generateId(),
      name: nameKey ? item[nameKey] : item,
      type: typeValue,
    }));

  const DPList = [
    {
      name: "Data Point Type",
      DPValue: createDPValue(DataPointType, null, "dataPointType"),
    },
    {
      name: "Data Point UI Group",
      DPValue: createDPValue(DataPointUIGroup, null, "dataPointUiGroup"),
    },
    {
      name: "Media Type",
      DPValue: createDPValue(MediaType, null, "mediaType"),
    },
    {
      name: "Supported",
      DPValue: createDPValue(Supported, null, "Supported"),
    },
  ];
  return {
    DPList: DPList.filter((filter) => filter?.DPValue?.length),
  };
};

export const getJavascriptListViewV2 = (collector) => {
  const obj = {
    label: collector?.name,
    value: collector?.id,
    category: collector?.category,
    playerDescription: collector?.playerDescription,
    adFrameworkName: collector?.adFrameworkName,
  };
  return obj;
};
export const changeLogTblColumns = [
  {
    key: "change_type",
    label: "Change Type",
  },
  {
    key: "Previous_Value",
    label: "Previous Value",
  },
  {
    key: "New_Value",
    label: "New Value",
  },
];

export function isValidDomain(domain) {
  const regex = /^(?!:\/\/)([a-zA-Z0-9-_]{1,63}\.)+[a-zA-Z]{2,}$/i;
  return regex.test(domain);
}

export const createFilterObjectArray = (values, keyName, query) => {
  return values
    ?.filter(
      (value) =>
        value !== null &&
        value !== undefined &&
        value.toString().trim?.() !== "",
    )
    ?.map((value) => {
      // Special handling for "project"
      if (keyName === "project" && Array.isArray(value)) {
        return {
          key: keyName,
          name: value.name === "blanks" ? "(Blanks)" : value?.name,
          id: value.id, // project id
          company_id: value.company_id, // optional for tracking
          type: keyName,
        };
      }

      let finalValue =
        typeof value === "object" && value !== null ? value?.id : value;
      if (value === true) finalValue = "Active";
      if (value === false) finalValue = "Paused";
      if (value === "blanks") finalValue = "(Blanks)";
      return {
        key: keyName,
        name: keyName === "project" ? value?.name : finalValue,
        id: keyName === "project" ? value?.id : finalValue,
        type: keyName,
      };
    });
};

export const filterBoxDataV3 = (collectorFilters) => {
  const createDPValue = (items, nameKey, typeValue) =>
    items
      ?.filter((item) => item != null)
      .map((item) => {
        let value = item;
        if (typeValue === "status") {
          // Convert boolean to Yes/No
          value = item === true ? "Active" : item === false ? "Paused" : item;
        }

        return {
          id: nameKey && item[nameKey] != null ? item[nameKey] : value,
          name: nameKey && item[nameKey] != null ? item[nameKey] : value,
          type: typeValue,
        };
      });

  const DPList = [];

  const dpNameMap = {
    collector: "Collector",
    mediaplayer: "Media Player",
    adframework: "Ad Framework",
    status: "Status",
  };

  for (const [key, value] of Object.entries(collectorFilters)) {
    if (value?.length && dpNameMap[key]) {
      let nameKey;
      if (value[0] && typeof value[0] === "object") {
        nameKey = Object.keys(value[0])[0];
      }

      DPList.push({
        name: dpNameMap[key],
        DPValue: createDPValue(value, nameKey, key),
        quickFilter: true,
      });
    }
  }

  return { DPList };
};

export const mergeFilterItems = (
  currentList = {},
  items = [],
  selectedFilterPopOver = [],
) => {
  const updated = { ...currentList };

  items.forEach((item) => {
    if (
      ["selected", "type", "Context"].includes(item.type) &&
      item.name === "(Blanks)"
    ) {
      return;
    }

    updated[item.type] = updated[item.type] || [];
    if (!updated[item.type]?.some((i) => i.id === item.id)) {
      updated[item.type].push(item);
    }
  });

  const allowedTypes = new Set(selectedFilterPopOver.map((f) => f.type));

  Object.keys(updated).forEach((type) => {
    if (!allowedTypes?.has(type)) {
      delete updated[type];
    }
  });

  return updated;
};

export const getFilterUniqueList = (key, collectorFilters, projectLists) => {
  const filterObject = {
    collector: [],
    mediaplayer: [],
    status: [],
    project: [],
    collectorconfigname: [],
    adframework: [],
  };
  if (!Object.keys(filterObject).includes(key)) return [];

  const source =
    key === "project"
      ? projectLists
      : collectorFilters?.[key] || collectorFilters;
  const filterObjects = createFilterObjectArray(source, key);

  const seen = new Set();
  const uniqueList =
    filterObjects
      ?.filter((item) => {
        const uniqueKey = item?.id ?? item?.name ?? JSON.stringify(item);
        if (seen.has(uniqueKey)) return false;
        seen.add(uniqueKey);
        return true;
      })
      ?.map((item) => ({
        key,
        name: item?.name,
        id: item?.id,
        type: key,
      })) || [];

  return uniqueList;
};

export const updatedFilterListForHeader = (
  dataArray,
  key,
  collectorFilters,
  query,
) => {
  if (
    !Array.isArray(dataArray) ||
    !key ||
    (!collectorFilters && key !== "project")
  )
    return [];

  const seen = new Set();

  const uniqueList = dataArray
    .map((item) => {
      if (!item) return null;

      let name, obj;
      switch (key) {
        case "collector": {
          const collector = item?.collectorConfig;
          const isCDN = item?.category === "CDN";
          name = isCDN ? "CDN" : collector?.collectorName;
          if (!name) return null;

          obj = {
            key,
            type: key,
            icon: collector?.collectorIcon || null,
            category: item?.category || null,
            adFramework: item?.adFramework?.adFrameworkName || null,
            adFrameworkIcon: item?.adFramework?.adFrameworkIcon || null,
          };
          break;
        }

        case "adframework": {
          const adFramework = item?.adFrameworkConfig;
          name = adFramework?.adFrameworkName || "(Blanks)";
          if (!name) return null;

          obj = {
            key,
            type: key,
            id: `${name}_adframework`,
            icon: adFramework?.adFrameworkIcon || null,
            collector: item?.collector?.collectorName || null,
            collectorIcon: item?.collector?.collectorIcon || null,
            category: item?.category || null,
          };
          break;
        }

        case "collectorconfigname": {
          name = item?.configName?.value;
          if (!name) return null;

          obj = { key, type: key };
          break;
        }

        case "status": {
          const status = item?.status;
          if (!status || typeof status.value !== "boolean") return null;

          name = status.value ? "Paused" : "Active";

          obj = {
            key,
            name,
            id: name,
            type: key,
            value: status.value,
            view: status.view,
          };
          break;
        }

        case "project": {
          const project = item?.project;
          name = project?.name || "(Blanks)";
          if (!name) return null;

          obj = {
            key,
            name,
            id: project?.value,
            type: key,
            value: project?.value ?? null,
          };
          break;
        }

        case "mediaplayer": {
          const mp = item?.mediaPlayerConfig;
          name = mp?.mediaPlayerName || "(Blanks)";
          if (!name) return null;

          obj = {
            key,
            type: key,
            icon: mp?.mediaPlayerIcon || null,
            plugin: mp?.plugin ?? false,
          };
          break;
        }

        default: {
          const value = item?.[key];
          if (!value) return null;

          name =
            value?.name ||
            value?.Name ||
            value?.value ||
            value?.datapipeName ||
            null;
          if (!name) return null;

          obj = { key, type: key };
        }
      }

      if (key !== "project" && key !== "status" && name !== "(Blanks)") {
        const normalizedName = name?.toLowerCase()?.replace(/\s+/g, "");
        if (normalizedName) {
          const matchedFilter = collectorFilters?.find(
            (f) => f && f?.toLowerCase().replace(/\s+/g, "") === normalizedName,
          );
          if (!matchedFilter) return null;
          obj.name = matchedFilter;
          obj.id = matchedFilter;
        }
      } else {
        obj.name = name;
        if (key !== "project") {
          obj.id = name;
        }
        if (obj.name === "(Blanks)" && key === "project") {
          obj.id = "Blanks";
        }
      }

      return obj;
    })
    .filter(Boolean)
    .filter((item) => {
      const normalized = item.name?.toLowerCase()?.replace(/\s+/g, "");
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });

  return uniqueList;
};

export const createFilterDPUpdated = (
  dataArray,
  selectedFilterPopOver,
  filterUniqueList,
  collectorFilters,
  query,
) => {
  const isDataArrayValid = Array.isArray(dataArray) && dataArray.length > 0;
  if (!isDataArrayValid && query) return { DPList: [] };
  if (!Array.isArray(dataArray)) return { DPList: [] };

  const dpNameMap = {
    collector: "Collector",
    mediaplayer: "Media Player",
    adframework: "Ad Framework",
    status: "Status",
  };

  const createDPValue = (items, nameKey, typeValue) =>
    items
      ?.filter((item) => item != null)
      .map((item) => {
        let value = item;
        if (typeValue === "status") {
          value = item === true ? "Active" : item === false ? "Paused" : item;
        }
        return {
          id: value?.name ?? value,
          name: value?.name ?? value,
          type: typeValue,
          key: typeValue,
        };
      })
      ?.sort((a, b) => {
        const nameA = a.name?.toString().toLowerCase() ?? "";
        const nameB = b.name?.toString().toLowerCase() ?? "";
        return nameA.localeCompare(nameB);
      });

  const hasAnyUniqueFilter = filterUniqueList
    ? Object.values(filterUniqueList).some(
        (arr) => Array.isArray(arr) && arr?.length > 0,
      )
    : false;

  const DPList = Object.keys(dpNameMap)
    .map((key) => {
      const displayName = dpNameMap[key];
      let DPValue = [];

      if (hasAnyUniqueFilter) {
        const uniqueListForKey =
          filterUniqueList?.[displayName.toLowerCase().replace(/\s+/g, "")];
        if (Array.isArray(uniqueListForKey) && uniqueListForKey?.length > 0) {
          let nameKey;
          if (uniqueListForKey[0] && typeof uniqueListForKey[0] === "object") {
            nameKey = Object.keys(uniqueListForKey[0])[0];
          }
          DPValue = createDPValue(uniqueListForKey, nameKey, key);
        } else {
          const listFromData = updatedFilterListForHeader(
            dataArray,
            key,
            collectorFilters[key],
          );
          if (Array.isArray(listFromData) && listFromData?.length > 0) {
            let nameKey;
            if (listFromData[0] && typeof listFromData[0] === "object") {
              nameKey = Object.keys(listFromData[0])[0];
            }
            DPValue = createDPValue(listFromData, nameKey, key);
          }
        }
      } else if (query && isDataArrayValid) {
        const listFromData = updatedFilterListForHeader(
          dataArray,
          key,
          collectorFilters[key],
        );
        if (Array.isArray(listFromData) && listFromData?.length > 0) {
          let nameKey;
          if (listFromData[0] && typeof listFromData[0] === "object") {
            nameKey = Object.keys(listFromData[0])[0];
          }
          DPValue = createDPValue(listFromData, nameKey, key);
        }
      } else if (!query) {
        const collectorList = collectorFilters?.[key];
        if (Array.isArray(collectorList) && collectorList?.length > 0) {
          let nameKey;
          if (collectorList[0] && typeof collectorList[0] === "object") {
            nameKey = Object.keys(collectorList[0])[0];
          }
          DPValue = createDPValue(collectorList, nameKey, key);
        }
      }

      if (!DPValue?.length) return null;

      return {
        name: displayName,
        DPValue,
        quickFilter: true,
      };
    })
    .filter(Boolean);
  return { DPList };
};

export const replaceBlanksInKeys = (
  data,
  keysToReplace = ["adframework", "mediaplayer", "project"],
) => {
  if (!data || typeof data !== "object") return data;

  const updatedData = { ...data };

  keysToReplace.forEach((key) => {
    if (Array.isArray(updatedData[key])) {
      updatedData[key] = updatedData[key].map((value) => {
        return value === "(Blanks)" || value === "--" ? "Blanks" : value;
      });
    }
  });

  return updatedData;
};

export const configSubTitleNaming = (data, isConnectorConfig) => {
  const hasPlugin = !!data.plugin;
  const hasAdFramework = data?.adFramework && data.adFramework !== "None";
  let result = data.collectorName;

  if (hasPlugin) {
    result += ` with ${data.mediaPlayerName}`;
    if (hasAdFramework) {
      result += ` and ${data.adFramework}`;
    }
  }
  if (isConnectorConfig) {
    result = data?.player?.name;
  }
  return result;
};

function sortByCollectorType(list = []) {
  const priority = {
    APP_COLLECTOR: 1,
    MEDIA_PLAYER: 2,
  };

  const defaultPriority = Object.keys(priority).length + 1;

  return [...list]?.sort((a, b) => {
    const pA = priority[a?.collectorType] ?? defaultPriority;
    const pB = priority[b?.collectorType] ?? defaultPriority;
    return pA - pB;
  });
}

export const getComponentName = (version) => {
  if (version?.collectorType !== "PLUGIN") {
    return version?.component;
  }

  const name = version?.component || "";
  const cleaned = name
    ?.replace(/\bMedia\b/gi, "")
    ?.replace(/\bPlayer\b/gi, "")
    ?.trim();

  return `${cleaned} Media Player`;
};

export const getOrderedSdkComponentVersion = (data) => {
  if (!data?.componentVersionDetails) return [];

  const orderedSdkType = [
    "BASE",
    "PLUGIN",
    "AD_FRAMEWORK",
    "EXTERNAL",
    "EXTRA_EXTENSION",
  ];
  const sortedData = sortBySdkType(
    data?.componentVersionDetails,
    orderedSdkType,
  );

  return sortedData;
};

function sortBySdkType(data, orderedSdkType) {
  return [...data].sort(
    (a, b) =>
      orderedSdkType.indexOf(a.collectorType) -
      orderedSdkType.indexOf(b.collectorType),
  );
}
